import React, { createContext, useState, useContext, useCallback, useRef } from 'react';

// ToastContext 생성
const ToastContext = createContext(null);

/**
 * ToastProvider 컴포넌트
 * 
 * 토스트 메시지를 전역적으로 관리하는 Provider입니다.
 * 여러 메시지를 동시에 표시하고 각각 자동으로 사라지게 합니다.
 * 
 * 사용법:
 * 1. App.js나 index.js에서 ToastProvider로 앱을 감싸기
 * 2. useToast() 훅을 사용하여 메시지 표시
 * 
 * 예시:
 * const { showAlert } = useToast();
 * showAlert('저장되었습니다', 'success');
 */
export const ToastProvider = ({ children }) => {
  // 현재 표시 중인 메시지들 (배열로 관리, 여러 개 동시 표시 가능)
  const [currentMessages, setCurrentMessages] = useState([]);
  
  // 각 메시지의 타이머를 저장하는 Map (메시지 ID를 key로 사용)
  const timersRef = useRef(new Map());

  // Confirm 다이얼로그 상태
  const [confirmDialog, setConfirmDialog] = useState(null);
  
  // Confirm 다이얼로그의 resolve 함수를 저장하는 ref
  const confirmResolveRef = useRef(null);

  /**
   * 특정 메시지를 제거하는 함수
   * 
   * @param {string|number} messageId - 제거할 메시지의 ID
   */
  const removeMessage = useCallback((messageId) => {
    setCurrentMessages(prev => {
      // 해당 메시지 제거
      const filtered = prev.filter(msg => msg.id !== messageId);
      
      // 타이머가 있으면 제거
      const timer = timersRef.current.get(messageId);
      if (timer) {
        clearTimeout(timer);
        timersRef.current.delete(messageId);
      }
      
      return filtered;
    });
  }, []);

  /**
   * 토스트 메시지 표시 함수 
   * 
   * @param {string} message - 표시할 메시지
   * @param {string} type - 메시지 타입 ('success', 'error', 'warning', 'info')
   * @param {number} duration - 표시 시간 (밀리초, 기본값: 3000)
   */
  const showAlert = useCallback((message, type = 'info', duration = 3000) => {
    // 새로운 메시지 객체 생성
    const newMessage = {
      id: Date.now() + Math.random(), // 고유 ID (타임스탬프 + 랜덤)
      message,
      type,
      duration,
    };

    // 즉시 표시 목록에 추가
    setCurrentMessages(prev => [...prev, newMessage]);

    // 자동으로 사라지게 하기 위한 타이머 설정
    const timer = setTimeout(() => {
      removeMessage(newMessage.id);
    }, duration);

    // 타이머 저장
    timersRef.current.set(newMessage.id, timer);
  }, [removeMessage]);

  /**
   * 특정 메시지를 수동으로 닫기
   * 
   * @param {string|number} messageId - 닫을 메시지의 ID
   */
  const closeToast = useCallback((messageId) => {
    removeMessage(messageId);
  }, [removeMessage]);

  /**
   * Confirm 다이얼로그 표시 함수
   * 
   * @param {string} message - 확인 메시지
   * @param {string} type - 메시지 타입 ('success', 'error', 'warning', 'info')
   * @param {string} confirmText - 확인 버튼 텍스트 (기본값: '확인')
   * @param {string} cancelText - 취소 버튼 텍스트 (기본값: '취소')
   * @returns {Promise<boolean>} - 확인(true) 또는 취소(false)
   * 
   * 사용 예시:
   * const { showConfirm } = useToast();
   * const result = await showConfirm('정말 삭제하시겠습니까?', 'warning');
   * if (result) {
   *   // 확인 버튼 클릭 시 실행
   * } else {
   *   // 취소 버튼 클릭 시 실행
   * }
   */
  const showConfirm = useCallback((message, type = 'info', confirmText = '확인', cancelText = '취소') => {
    return new Promise((resolve) => {
      // resolve 함수를 ref에 저장
      confirmResolveRef.current = resolve;
      
      // 다이얼로그 표시
      setConfirmDialog({
        message,
        type,
        confirmText,
        cancelText,
      });
    });
  }, []);

  /**
   * Confirm 다이얼로그 닫기 (확인)
   */
  const handleConfirm = useCallback(() => {
    if (confirmResolveRef.current) {
      confirmResolveRef.current(true);
      confirmResolveRef.current = null;
    }
    setConfirmDialog(null);
  }, []);

  /**
   * Confirm 다이얼로그 닫기 (취소)
   */
  const handleCancel = useCallback(() => {
    if (confirmResolveRef.current) {
      confirmResolveRef.current(false);
      confirmResolveRef.current = null;
    }
    setConfirmDialog(null);
  }, []);

  // 컴포넌트 언마운트 시 모든 타이머 정리
  React.useEffect(() => {
    // cleanup 함수에서 사용할 ref의 현재 값을 변수에 저장
    const timersMap = timersRef.current;
    
    return () => {
      // 모든 타이머 제거
      timersMap.forEach(timer => {
        clearTimeout(timer);
      });
      timersMap.clear();
    };
  }, []);

  // Context에 전달할 값
  const value = {
    currentMessages, // 배열로 변경
    showAlert,
    closeToast,
    showConfirm,
    confirmDialog,
    handleConfirm,
    handleCancel,
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
    </ToastContext.Provider>
  );
};

/**
 * useToast 커스텀 훅
 * 
 * 토스트 메시지와 Confirm 다이얼로그를 표시하기 위한 훅입니다.
 * 
 * @returns {Object} { showAlert, closeToast, currentMessages, showConfirm }
 * - showAlert: 메시지를 표시하는 함수
 * - closeToast: 특정 메시지를 닫는 함수 (messageId 필요)
 * - currentMessages: 현재 표시 중인 메시지 배열
 * - showConfirm: Confirm 다이얼로그를 표시하는 함수 (Promise 반환)
 * 
 * 사용 예시:
 * const { showAlert, showConfirm } = useToast();
 * 
 * // Alert 사용
 * showAlert('저장되었습니다', 'success');
 * 
 * // Confirm 사용
 * const result = await showConfirm('정말 삭제하시겠습니까?', 'warning');
 * if (result) {
 *   // 확인 버튼 클릭
 * } else {
 *   // 취소 버튼 클릭
 * }
 */
export const useToast = () => {
  const context = useContext(ToastContext);
  
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  
  return context;
};

